import { useCallback, useEffect, useRef } from 'react';
import { type DataFunc } from 'react-mentions';

import { type MentionsData } from '~/components/DiscussionsV2/DiscussionForm';

type MentionSuggestion = MentionsData & { id: string };
type PendingMentionQuery = { query: string; callback: Parameters<DataFunc>[1] };

const isMentionSuggestion = (member: MentionsData): member is MentionSuggestion => member.id !== null;

const findMentionSuggestions = (members: MentionsData[], query: string) => {
  const normalizedQuery = query.toLowerCase();

  return members
    .filter(isMentionSuggestion)
    .filter(({ display, id }) => (display || id).toLowerCase().includes(normalizedQuery));
};

// react-mentions snapshots `data` when `@` is typed and never refreshes it, so an `@` typed before members load
// never shows the suggestions list. Passing `data` as a function lets us hold the query until members arrive.
export const useDiscussionMentionSuggestions = ({
  mentionsList,
  isLoadingMentions,
}: {
  mentionsList: MentionsData[];
  isLoadingMentions: boolean;
}) => {
  const pendingQuery = useRef<PendingMentionQuery>();

  const cancelMentionSuggestions = useCallback(() => {
    pendingQuery.current = undefined;
  }, []);

  const getMentionSuggestions = useCallback<DataFunc>(
    (query, callback) => {
      if (isLoadingMentions) {
        pendingQuery.current = { query, callback };
        return;
      }
      pendingQuery.current = undefined;
      return findMentionSuggestions(mentionsList, query);
    },
    [isLoadingMentions, mentionsList],
  );

  useEffect(() => {
    if (isLoadingMentions || !pendingQuery.current) {
      return;
    }
    const request = pendingQuery.current;
    pendingQuery.current = undefined;
    request.callback(findMentionSuggestions(mentionsList, request.query));
  }, [isLoadingMentions, mentionsList]);

  useEffect(() => cancelMentionSuggestions, [cancelMentionSuggestions]);

  return { getMentionSuggestions, cancelMentionSuggestions };
};
